import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';
import { GlobalService } from './global.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { defer, finalize, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { WorkspaceService } from '../../workspace/workspace.service';
import { ActionResponse } from '../../shared/_models/action-response.model';

@Injectable({
  providedIn: 'root',
})
export class JsonObjectFactoryService {
  private checkLockedbj: any[] = [];
  durationInSeconds = 4;
  /**
   * Identical GETs that overlap because a host is destroyed and recreated
   * during one navigation. `refCount: false` keeps the single HTTP alive
   * after the first subscriber unsubscribes. Efile reads that show up more
   * than once on that navigation (`siao78`, `j4k0an`, dashboard sheets,
   * `getDashboardByKey`) stay reusable for a short window after completion
   * so the replacement host does not post again. Every other GET is shared
   * only while the request is still in flight.
   */
  private readonly sharedGets = new Map<string, Observable<any>>();
  private static readonly SHARED_GET_HOLD_MS = 1000;
  private static readonly HOLD_AFTER_COMPLETE = /siao78|j4k0an|i6k670|89lhyq|zqe86k|oisavn|sjrs2c|getDashboardByKey/;

  constructor(
    private http: HttpClient,
    private globalService: GlobalService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private workspaceFactory: WorkspaceService
  ) {}

  saveJSON(_url: string, _data: any) {
    const filterParams = this.workspaceFactory.workspaceFactory.activeScreen.filters
      ? this.workspaceFactory.workspaceFactory.activeScreen.filters.getFilterParams()
      : {};

    const rowData =
      this.workspaceFactory.workspaceFactory.activeScreen.data?.selected?.rowData;

    this.checkLockedbj = [];
    if (_.isArray(rowData) && rowData.length > 0) {
      _.each(rowData, (item: any) => {
        this.checkLockedbj.push({
          leid: item.data.LEID || item.data.SOURCE_LEID,
          me_code: item.data.ME_CODE || item.data.ME || item.data.SOURCE_ME_CODE,
          cdr_number: item.data.CDR_NO || item.data.TAX_CODE || item.data.SOURCE_CDR_NO,
          consol_group_key: item.data.CONSOL_GROUP_KEY || item.data.FILING_GROUP,
          reporting_period: item.data.REPORTING_PERIOD || item.data.SOURCE_REPORTING_PERIOD,
          combination_key: item.data.COMBINATION_KEY || item.data.COMPL_COMBINATION_KEY || item.data.HO_COMBINATION_KEY,
          scenario_key: item.data.SCENARIO_KEY || _data.scenario || filterParams.scenario,
        });
      });
    } else if (rowData) {
      this.checkLockedbj.push({
        leid: rowData.LEID || rowData.SOURCE_LEID,
        me_code: rowData.ME_CODE || rowData.ME || rowData.SOURCE_ME_CODE,
        cdr_number: rowData.CDR_NO || rowData.TAX_CODE || rowData.SOURCE_CDR_NO,
        consol_group_key: rowData.CONSOL_GROUP_KEY || rowData.FILING_GROUP,
        reporting_period: rowData.REPORTING_PERIOD || rowData.SOURCE_REPORTING_PERIOD,
        combination_key: rowData.COMBINATION_KEY || rowData.COMPL_COMBINATION_KEY || rowData.HO_COMBINATION_KEY,
        scenario_key: rowData.SCENARIO_KEY || _data.scenario || filterParams.scenario,
      });
    } else {
      this.checkLockedbj.push({
        scenario_key: _data.scenario || filterParams.scenario,
      });
    }

    _data.tax_year = _data.tax_year || filterParams.tax_year;
    _data.scenario = _data.scenario || filterParams.scenario;
    _data.jcd_key = this.globalService.global.globalParams.jcd_key;
    _data.issue_id = this.globalService.global.globalParams.issue_id;
    _data.issue_short_desc = this.globalService.global.globalParams.issue_short_desc;
    _data.checkLockedbj = JSON.stringify(this.checkLockedbj);

    if (
      this.workspaceFactory.workspaceFactory.activeScreen?.filters &&
      typeof this.workspaceFactory.workspaceFactory.activeScreen.filters.getFilters === 'function'
    ) {
      const foundScenario: any = _.find(
        this.workspaceFactory.workspaceFactory.activeScreen.filters.getFilters(),
        { param_name: 'scenario' }
      );
      if (foundScenario?.selected) {
        _data.scenarioCode = foundScenario.selected.CODE;
        _data.is_issue_key_enabled = foundScenario.selected.ISSUE_ID_ENABLED;
      }
    }

    const data = new FormData();
    _.map(_data, (v: any, k: any) => {
      if (_data[k] !== null && _data[k] !== undefined && _data[k] !== '') {
        data.append(k, v);
      }
    });

    return this.http.post(_url, data).pipe(
      map((response: any) => {
        if (response.callSuccess == '1') {
          return response;
        }
        this.openSnackBar(
          'The requested data could not be saved. If this continues please contact support.',
          'gtw-snackbar--red'
        );
        return response;
      })
    );
  }

  getJsonObj(_url: any, _params: any, filter: any = undefined, grp_key: any = undefined) {
    return this.getJSONObj(_url, _params, filter, grp_key);
  }

  getJSONObj(_url: any, _params: any, filter: any = undefined, grp_key: any = undefined, options?: { silent?: boolean; fresh?: boolean }) {
    const filterParams = this.workspaceFactory.workspaceFactory.activeScreen.filters
      ? this.workspaceFactory.workspaceFactory.activeScreen.filters.getFilterParams()
      : {};
    const paramsMerge = _.merge({}, filterParams, _params);
    const params = _.merge({}, this.globalService.global.globalParams, paramsMerge);
    delete params.checkLockedbj;

    if (filterParams.tax_year?.length) {
      if (!filterParams.scenario) {
        if (this.globalService.getScenario(params.tax_year)) {
          params.scenario = this.globalService.getScenario(params.tax_year);
        }
      }
    }

    if (_url.includes('/loadPivotFormJson')) {
      return this.filterPivotFormJson(_url, params);
    }

    if (_url.includes('getDashboardData')) {
      return this.filterApiPostCall(_url, params, options?.silent);
    }

    return this.filterApiCall(_url, params, options?.silent, options?.fresh);
  }

  /**
   * Executive dashboard sheet endpoints (`getDashboardData/{id}`) expect POST
   * with filter params in the body (see ui dashboardService.js loadSheets).
   * GET returns 405 on stage/prod gateways.
   */
  filterApiPostCall(_url: string, params: Record<string, any>, silent: boolean = false) {
    return this.http.post<any>(_url, params).pipe(
      switchMap((response: any) => this.normalizeActionResponse(response, silent))
    );
  }

  private normalizeActionResponse(response: any, silent: boolean) {
    if (Array.isArray(response)) {
      return of({ callSuccess: '1', jsonObject: response } as ActionResponse);
    }

    const r: any = response ?? {};
    const success = r.callSuccess !== undefined ? r.callSuccess : r['callSuccess '];
    if (success == '1' || success === 1) {
      return of(response as ActionResponse);
    }

    // Legacy dashboard payloads may omit callSuccess and return jsonObject/data only.
    if (Array.isArray(r.jsonObject)) {
      return of({ ...r, callSuccess: '1', jsonObject: r.jsonObject } as ActionResponse);
    }
    if (Array.isArray(r.data)) {
      return of({ ...r, callSuccess: '1', jsonObject: r.data } as ActionResponse);
    }

    if (!silent) {
      this.openSnackBar(
        'The requested data could not be delivered. If this continues please contact support.',
        'gtw-snackbar--red'
      );
    }
    return throwError(() => 'Action call failed!');
  }

  /**
   * One shared GET for the same URL and params. See `sharedGets`.
   */
  shareGet<T>(key: string, fresh: boolean, factory: () => Observable<T>): Observable<T> {
    if (fresh) {
      this.sharedGets.delete(key);
    }
    const existing = this.sharedGets.get(key) as Observable<T> | undefined;
    if (existing) {
      return existing;
    }

    const shared = defer(factory).pipe(
      tap({
        error: () => this.sharedGets.delete(key),
      }),
      finalize(() => {
        const drop = () => {
          if (this.sharedGets.get(key) === shared) {
            this.sharedGets.delete(key);
          }
        };
        if (JsonObjectFactoryService.HOLD_AFTER_COMPLETE.test(key)) {
          window.setTimeout(drop, JsonObjectFactoryService.SHARED_GET_HOLD_MS);
        } else {
          drop();
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.sharedGets.set(key, shared);
    return shared;
  }

  private buildGetKey(url: string, params: any): string {
    const source = params && typeof params === 'object' ? params : {};
    const encoded = Object.keys(source)
      .sort()
      .filter((key) => source[key] !== undefined && source[key] !== null)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(source[key]))}`)
      .join('&');
    return `${url}|${encoded}`;
  }

  filterApiCall(_url: any, params: any, silent: boolean = false, fresh = false) {
    if (String(_url).includes('getDashboardData')) {
      return this.filterApiPostCall(_url, params, silent);
    }

    return this.shareGet(this.buildGetKey(_url, params), fresh, () => this.http.get<ActionResponse>(_url, { params }).pipe(
      switchMap((response: ActionResponse) => this.normalizeActionResponse(response, silent))
    ));
  }

  filterPivotFormJson(_url: any, params: any) {
    return this.http.get<any>(_url, { params }).pipe(
      switchMap((response: any) => {
        if (response.irsForm?.callSuccess == '1') {
          return of(response);
        }
        this.openSnackBar(
          'The requested data could not be delivered. If this continues please contact support.',
          'gtw-snackbar--red'
        );
        return throwError('Action call failed!');
      })
    );
  }

  openSnackBar(message: string, panelClass: string = '') {
    this.gtwSnackbar.openSnackBar(message, (panelClass || '') as any, true, this.durationInSeconds);
  }

  arrObjToLower(_data: any): any[] {
    return _.map(_data, (x: any) =>
      _.transform(x, (result: any, val: any, key: any) => {
        if (key != undefined) { result[_.toLower(key)] = val; }
      }, {} as any)
    ) as any[];
  }

  objToLower(_data: any): any {
    return _.transform(_data, (result: any, val: any, key: any) => {
      result[key.toLowerCase()] = val;
    }, {} as any);
  }

  arrObjToUpper(_data: any): any[] {
    return _.map(_data, (x: any) =>
      _.transform(x, (result: any, val: any, key: any) => {
        result[key.toUpperCase()] = val;
      }, {} as any)
    ) as any[];
  }
}
